import { ROLES } from '@/utils/constants';
import { getLastPathFromUrl } from '@/utils/strings';

export const ROUTES = {
  LOGIN: '/auth/login',
  CHANGE_PASSWORD: '/auth/changepass',
  RESET_PASSWORD: '/auth/reset-password',
  SIGN_UP: '/auth/signup',
  DASHBOARD: '/',
  CASES: '/cases',
  NEW_CASE: '/cases/new-case',
  CASE_DETAIL: '/cases/:caseId',
  PROCESS_CASE: '/cases/process-case/:caseId',
  GRADING: '/grading',
  GRADING_CASE: '/grading/:caseId',
  GRADING_CASE_STEP_1: '/grading/:caseId/get-started',
  GRADING_CASE_STEP_2: '/grading/:caseId/readable_imgs',
  GRADING_CASE_STEP_3: '/grading/:caseId/questions',
  GRADING_CASE_STEP_4: '/grading/:caseId/signature',
  USERS: '/users',
  BILLING: '/billing',
  LEGAL: '/legal',
  KITCHEN_SINK: '/kitchen-sink',
  PROFILE: '/profile',
  TWO_FACTOR_AUTH_ENABLE: '/profile/2fa/enable',
  TWO_FACTOR_AUTH_DISABLE: '/profile/2fa/disable',
  LOGOUT: '/logout',
  ADMIN: '/admin',
  ADMIN_USERS: '/admin/users',
  ADMIN_USERS_APPROVAL: '/admin/users/approval/:userEmail',
  ADMIN_USERS_EDIT: '/admin/users/edit/:userId',
  ADMIN_CLINICS: '/admin/clinics',
  ADMIN_ADD_CLINIC: '/admin/clinics/new',
  ADMIN_EDIT_CLINIC: '/admin/clinics/edit',
  ADMIN_ADD_CLINIC_SITE: '/admin/clinics/:clinicId/site/new',
  ADMIN_EDIT_CLINIC_SITE: '/admin/clinics/:clinicId/site/edit/:siteId',
  ADMIN_EDIT_SITE_NPI_PREFERRED: '/admin/clinics/:clinicId/site/edit/:siteId/preferred-npi',
  ADMIN_TELEHEALTH_CLINICIANS: '/admin/telehealth-clinicians',
  ADMIN_AI_SEARCH: '/admin/ai-search',
  SALES: '/sales',
  INSIGHTS: '/insights',
  INSIGHTS_CASE: '/insights/:caseId',
  INSIGHTS_DR_FORM: '/insights/:caseId/dr-form',
  INSIGHTS_DR_FORM_STEP_1: '/insights/:caseId/dr-form/questions',
  INSIGHTS_DR_FORM_STEP_2: '/insights/:caseId/dr-form/follow-up',
  DASHBOARD_CASES_CHART: '/dashboard/cases-chart',
  // Print routes (standalone, no auth layout)
  PRINT_TELEHEALTH_REPORT: '/print/telehealth-report/:gradeId',
};

// we have some restricting routes that we should not allow the user to access
export const RESTRICTED_ROUTES: Record<ROLES, string[]> = {
  clinicuser: [
    ROUTES.DASHBOARD,
    ROUTES.PROFILE,
    ROUTES.LEGAL,
    ROUTES.TWO_FACTOR_AUTH_ENABLE,
    ROUTES.TWO_FACTOR_AUTH_DISABLE,
  ],
  clinicadmin: [
    ROUTES.DASHBOARD,
    ROUTES.USERS,
    ROUTES.BILLING,
    ROUTES.PROFILE,
    ROUTES.LEGAL,
    ROUTES.TWO_FACTOR_AUTH_ENABLE,
    ROUTES.TWO_FACTOR_AUTH_DISABLE,
  ],
  // telehealth_clinician && test_grader have the same restricted routes, but inside the routes they have different permissions
  telehealthclinician: [
    ROUTES.GRADING,
    ROUTES.PROFILE,
    ROUTES.LEGAL,
    ROUTES.TWO_FACTOR_AUTH_ENABLE,
    ROUTES.TWO_FACTOR_AUTH_DISABLE,
  ],
  testgrader: [
    ROUTES.GRADING,
    ROUTES.PROFILE,
    ROUTES.LEGAL,
    ROUTES.TWO_FACTOR_AUTH_ENABLE,
    ROUTES.TWO_FACTOR_AUTH_DISABLE,
  ],
  vitaziadmin: Array.from(Object.values(ROUTES)),
  optometrist: [
    ROUTES.INSIGHTS,
    ROUTES.INSIGHTS_DR_FORM,
    ROUTES.PROFILE,
    ROUTES.LEGAL,
    ROUTES.TWO_FACTOR_AUTH_ENABLE,
    ROUTES.TWO_FACTOR_AUTH_DISABLE,
    ROUTES.INSIGHTS_DR_FORM_STEP_1,
    ROUTES.INSIGHTS_DR_FORM_STEP_2,
  ],
  caseadmin: [
    ROUTES.DASHBOARD,
    ROUTES.CASES,
    ROUTES.NEW_CASE,
    ROUTES.CASE_DETAIL,
    ROUTES.PROCESS_CASE,
    ROUTES.PROFILE,
    ROUTES.LEGAL,
    ROUTES.TWO_FACTOR_AUTH_ENABLE,
    ROUTES.TWO_FACTOR_AUTH_DISABLE,
  ],
  sales: [
    ROUTES.SALES,
    ROUTES.PROFILE,
    ROUTES.LEGAL,
    ROUTES.TWO_FACTOR_AUTH_ENABLE,
    ROUTES.TWO_FACTOR_AUTH_DISABLE,
  ],
  sandbox: [
    ROUTES.INSIGHTS,
    ROUTES.PROFILE,
    ROUTES.LEGAL,
    ROUTES.TWO_FACTOR_AUTH_ENABLE,
    ROUTES.TWO_FACTOR_AUTH_DISABLE,
  ],
};

export const MAIN_ROUTE_BY_ROLE: Record<ROLES, string> = {
  clinicuser: ROUTES.DASHBOARD,
  clinicadmin: ROUTES.DASHBOARD,
  caseadmin: ROUTES.CASES,
  telehealthclinician: ROUTES.GRADING,
  testgrader: ROUTES.GRADING,
  vitaziadmin: ROUTES.CASES,
  optometrist: ROUTES.INSIGHTS,
  sales: ROUTES.SALES,
  sandbox: ROUTES.INSIGHTS,
};

export type ROUTE_BY_ROLE = keyof typeof RESTRICTED_ROUTES;

export const REDIRECT_ROUTES = ['/changepass', '/users/changepass'];

export const getRelativeRoute = (route: keyof typeof ROUTES) => {
  const path = ROUTES[route];
  return `./${getLastPathFromUrl(path)}`;
};
